import { apiFetch } from "@/lib/api";
import type { WorkspaceDto } from "@/lib/types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSession } from "./session-provider";

const STORAGE_KEY = "steptember-workspace";

interface WorkspaceContextValue {
  workspaces: WorkspaceDto[];
  loading: boolean;
  selectedWorkspaceId: string | null;
  selectedWorkspace: WorkspaceDto | null;
  canOrganize: boolean;
  setSelectedWorkspaceId: (workspaceId: string | null) => void;
  refresh: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function useWorkspace(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within WorkspaceProvider");
  }
  return context;
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { me, isAdmin } = useSession();
  const [workspaces, setWorkspaces] = useState<WorkspaceDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceState] = useState<string | null>(
    null,
  );

  const refresh = useCallback(async () => {
    if (
      me?.authenticated !== true ||
      (!isAdmin && me.workspaceAccessEnabled !== true)
    ) {
      setWorkspaces([]);
      setSelectedWorkspaceState(null);
      return;
    }

    setLoading(true);
    try {
      const data = await apiFetch<WorkspaceDto[]>("/api/workspaces");
      setWorkspaces(data);
      setSelectedWorkspaceState((current) => {
        if (data.length === 1) {
          window.localStorage.setItem(STORAGE_KEY, data[0].id);
          return data[0].id;
        }
        return current && data.some((workspace) => workspace.id === current)
          ? current
          : null;
      });
    } finally {
      setLoading(false);
    }
  }, [isAdmin, me?.authenticated, me?.workspaceAccessEnabled]);

  useEffect(() => {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    setSelectedWorkspaceState(saved && saved !== "all" ? saved : null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setSelectedWorkspaceId = useCallback((workspaceId: string | null) => {
    setSelectedWorkspaceState(workspaceId);
    window.localStorage.setItem(STORAGE_KEY, workspaceId ?? "all");
  }, []);

  const selectedWorkspace =
    workspaces.find((workspace) => workspace.id === selectedWorkspaceId) ?? null;
  const canOrganize =
    selectedWorkspace?.role === "Owner" ||
    selectedWorkspace?.role === "Administrator" ||
    selectedWorkspace?.role === "Organizer";

  const value = useMemo(
    () => ({
      workspaces,
      loading,
      selectedWorkspaceId,
      selectedWorkspace,
      canOrganize,
      setSelectedWorkspaceId,
      refresh,
    }),
    [
      workspaces,
      loading,
      selectedWorkspaceId,
      selectedWorkspace,
      canOrganize,
      setSelectedWorkspaceId,
      refresh,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}
